<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import logoCotap from '~/assets/images/cotap/COTAP Logo CMYK.png'
import logoCotapDiap from '~/assets/images/cotap/COTAP Logo CMYK diap.png'

const config = useAppConfig()
const route = useRoute()
const colorMode = useColorMode()

const open = ref(false)
const { isIntegrationsViewer } = useUser()
const homePath = computed(() => {
  if (isIntegrationsViewer.value) return '/integrations'
  return '/'
})

const logoSrc = computed(() =>
  colorMode.value === 'dark' ? logoCotapDiap : logoCotap
)

const allLinks = computed<NavigationMenuItem[][]>(() => [[
  {
    label: 'Home',
    icon: 'i-lucide-house',
    to: '/',
    exact: true,
    onSelect: () => { open.value = false }
  },
  {
    label: 'Servers',
    icon: 'i-lucide-server',
    to: '/servers',
    onSelect: () => { open.value = false }
  },
  {
    label: 'Kubernetes',
    icon: 'i-lucide-boxes',
    to: '/k8s',
    onSelect: () => { open.value = false }
  },
  {
    label: 'Integrations',
    icon: 'i-lucide-git-branch',
    to: '/integrations',
    onSelect: () => { open.value = false }
  },
  {
    label: 'Alerts',
    icon: 'i-lucide-triangle-alert',
    to: '/alerts',
    onSelect: () => { open.value = false }
  },
  {
    label: 'Vulnerabilities',
    icon: 'i-lucide-shield-alert',
    to: '/vulnerabilities',
    onSelect: () => { open.value = false }
  },
  {
    label: 'Jira teams',
    icon: 'i-lucide-list-checks',
    to: '/jira-teams',
    onSelect: () => { open.value = false }
  }
]])

const links = computed<NavigationMenuItem[][]>(() => {
  if (isIntegrationsViewer.value) {
    return allLinks.value.map(group => group.filter(l => l.to === '/integrations'))
  }
  return allLinks.value
})

const pageTitles: Record<string, string> = {
  '/': (config.monitoring as { title?: string })?.title || 'Monitoring Dashboard',
  '/servers': 'Servers',
  '/k8s': 'Kubernetes',
  '/integrations': 'Integrations',
  '/alerts': 'Alerts',
  '/vulnerabilities': 'Vulnerabilities',
  '/users': 'Gebruikers',
  '/api-tokens': 'API tokens',
  '/process-flows': 'Process flows',
  '/entra-applications': 'Entra applications',
  '/jira-teams': 'Jira teams'
}

const pageTitle = computed(() => {
  const metaTitle = route.meta.title
  if (typeof metaTitle === 'string' && metaTitle) return metaTitle

  const exact = pageTitles[route.path]
  if (exact) return exact

  const match = Object.keys(pageTitles)
    .filter(path => path !== '/' && route.path.startsWith(path))
    .sort((a, b) => b.length - a.length)[0]

  return match ? pageTitles[match] : ((config.monitoring as { title?: string })?.title || 'Monitoring')
})

const ownPanel = computed(() => route.meta.ownPanel === true)
</script>

<template>
  <UDashboardGroup unit="rem">
    <UDashboardSidebar
      id="default"
      v-model:open="open"
      collapsible
      resizable
      :ui="{
        root: 'bg-default',
        footer: 'lg:border-t lg:border-default'
      }"
    >
      <template #header="{ collapsed }">
        <NuxtLink
          :to="homePath"
          class="flex items-center min-w-0 overflow-hidden"
          :class="collapsed ? 'justify-center w-full' : ''"
          @click="open = false"
        >
          <div :class="collapsed ? 'h-8 w-8 overflow-hidden shrink-0' : ''">
            <img
              :src="logoSrc"
              alt="COTAP"
              class="h-8 w-auto max-w-none object-contain object-left"
            >
          </div>
        </NuxtLink>
      </template>

      <template #default="{ collapsed }">
        <UNavigationMenu
          :collapsed="collapsed"
          :items="links[0]"
          orientation="vertical"
          tooltip
          color="primary"
          class="w-full"
        />
      </template>

      <template #footer="{ collapsed }">
        <UserMenu :collapsed="collapsed" />
      </template>
    </UDashboardSidebar>

    <slot v-if="ownPanel" />

    <UDashboardPanel
      v-else
      id="main"
    >
      <template #header>
        <UDashboardNavbar :title="pageTitle">
          <template #leading>
            <UDashboardSidebarCollapse />
          </template>
        </UDashboardNavbar>
      </template>

      <template #body>
        <slot />
      </template>
    </UDashboardPanel>
  </UDashboardGroup>
</template>
